import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { ClipboardDocumentIcon, GlobeAltIcon, KeyIcon } from "@heroicons/react/24/outline";
import { PlayIcon } from "@heroicons/react/24/solid";
import { Tooltip } from "react-tooltip";
import {
  API_ENDPOINTS,
  missingRequiredFields,
  placeholderForParam,
  resolveEndpointPath,
} from "../../constants/apiEndpoints";
import { apiTokensService } from "../../services/apiTokens";
import {
  buildSnippet,
  getExternalApiBaseUrl,
  runExternalApiRequest,
} from "../../services/externalApi";
import { useToast } from "../../contexts/ToastContext";
import WeaveLoader from "../common/WeaveLoader";
import FolderTabs from "../common/FolderTabs";
import SelectPopover from "../common/SelectPopover";
import { HttpStatus, MethodBadge, SECTION_LABEL } from "./shared";

const OUTPUT_TABS = [
  { id: "response", label: "Response" },
  { id: "curl", label: "curl" },
  { id: "python", label: "Python" },
  { id: "javascript", label: "JavaScript" },
];

const inputClass = "w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm text-theme-text placeholder:text-theme-secondary-text";

const ApiPlayground = () => {
  const { success: toastSuccess } = useToast();
  const currentProject = useSelector((state) => state.project.currentProject);
  const siteId = currentProject?.hash || "";

  const [tokens, setTokens] = useState([]);
  const [tokensLoaded, setTokensLoaded] = useState(false);
  const [selectedEndpointId, setSelectedEndpointId] = useState(API_ENDPOINTS[0]?.id);
  const [selectedTokenHash, setSelectedTokenHash] = useState("");
  const [fieldValues, setFieldValues] = useState({});
  const [outputTab, setOutputTab] = useState("curl");
  const [running, setRunning] = useState(false);
  const [response, setResponse] = useState(null);
  const [copied, setCopied] = useState("");
  const [sessionKey, setSessionKey] = useState("");
  const [sessionState, setSessionState] = useState("idle");

  const selectedEndpoint = useMemo(
    () => API_ENDPOINTS.find((e) => e.id === selectedEndpointId) || API_ENDPOINTS[0],
    [selectedEndpointId],
  );

  useEffect(() => {
    const loadTokens = () => {
      apiTokensService.listTokens(
        (res) => {
          const rows = res.data?.results || res.data || [];
          setTokens(Array.isArray(rows) ? rows : []);
          setTokensLoaded(true);
        },
        () => {
          setTokens([]);
          setTokensLoaded(true);
        },
      );
    };
    loadTokens();
    window.addEventListener("weave-api-tokens-changed", loadTokens);
    return () => window.removeEventListener("weave-api-tokens-changed", loadTokens);
  }, []);

  useEffect(() => {
    const active = tokens.filter((t) => t.is_active);
    if (!selectedTokenHash && active[0]) {
      setSelectedTokenHash(active[0].hash);
    } else if (selectedTokenHash && !active.find((t) => t.hash === selectedTokenHash)) {
      setSelectedTokenHash(active[0]?.hash || "");
    }
  }, [tokens, selectedTokenHash]);

  useEffect(() => {
    if (!selectedEndpoint) return;
    setFieldValues((prev) => {
      const next = { ...prev };
      selectedEndpoint.fields.forEach((field) => {
        if (field.type === "boolean" && next[field.key] === undefined) {
          next[field.key] = field.default === true;
        }
        if (field.source === "site" && siteId) {
          next[field.key] = siteId;
        }
        if (next[field.key] === undefined) {
          next[field.key] = field.default ?? "";
        }
      });
      return next;
    });
  }, [selectedEndpoint, siteId]);

  const activeTokens = tokens.filter((token) => token.is_active);
  const selectedToken = tokens.find((t) => t.hash === selectedTokenHash);

  const ensurePlaygroundSession = (tokenHash, { force = false } = {}) => {
    const storageKey = `playground-api-token:v2:${tokenHash}`;
    if (!force) {
      const cached = JSON.parse(sessionStorage.getItem(storageKey) || "null");
      if (cached?.token && new Date(cached.expiresAt).getTime() > Date.now() + 30_000) {
        setSessionKey(cached.token);
        setSessionState("ready");
        return Promise.resolve(cached.token);
      }
    }
    sessionStorage.removeItem(storageKey);
    setSessionKey("");
    setSessionState("loading");
    return new Promise((resolve, reject) => {
      apiTokensService.playgroundSession(
        tokenHash,
        (res) => {
          const token = res.data?.token || "";
          if (token) {
            sessionStorage.setItem(storageKey, JSON.stringify({
              token,
              expiresAt: res.data.expires_at,
            }));
          }
          setSessionKey(token);
          setSessionState(token ? "ready" : "error");
          if (token) resolve(token);
          else reject(new Error("No playground token"));
        },
        (err) => {
          setSessionKey("");
          setSessionState("error");
          reject(err);
        },
      );
    });
  };

  useEffect(() => {
    if (!selectedTokenHash || !selectedToken?.is_active) {
      setSessionKey("");
      setSessionState(selectedTokenHash ? "error" : "idle");
      return undefined;
    }
    let cancelled = false;
    ensurePlaygroundSession(selectedTokenHash).catch(() => {
      if (!cancelled) {
        setSessionKey("");
        setSessionState("error");
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTokenHash, selectedToken?.is_active]);

  const pathValues = {};
  const body = {};
  (selectedEndpoint?.fields || []).forEach((field) => {
    if (field.body) {
      if (field.type === "boolean") {
        body[field.key] = fieldValues[field.key] === true;
        return;
      }
      const value = (fieldValues[field.key] ?? "").toString().trim();
      if (value !== "") body[field.key] = value;
      return;
    }
    pathValues[field.key] = (fieldValues[field.key] || "").trim();
  });

  const displayPath = selectedEndpoint
    ? resolveEndpointPath(selectedEndpoint, pathValues, { usePlaceholders: true })
    : "/";
  const runPath = selectedEndpoint
    ? resolveEndpointPath(selectedEndpoint, pathValues)
    : "/";
  // Concatenate for display so path placeholders like {site_id} stay readable
  // (URL.toString() would percent-encode the braces).
  const displayUrl = `${getExternalApiBaseUrl()}${displayPath}`;
  const apiKey = sessionKey;
  const bodyPayload = Object.keys(body).length ? body : null;
  const snippet = outputTab === "response" ? "" : buildSnippet({
    mode: outputTab,
    method: selectedEndpoint?.method,
    url: displayUrl,
    apiKey: apiKey || "YOUR_API_KEY",
    body: bodyPayload,
  });

  const missingFields = missingRequiredFields(selectedEndpoint, fieldValues);
  const hasApiKey = Boolean(apiKey);
  let runDisabledReason = "";
  if (!hasApiKey) {
    runDisabledReason = sessionState === "loading"
      ? "Loading playground key"
      : "Add an API key to run requests";
  } else if (missingFields.length) {
    runDisabledReason = `Missing: ${missingFields.map((f) => f.label).join(", ")}`;
  } else if (running) {
    runDisabledReason = "Request in progress";
  }
  const canRun = hasApiKey && !missingFields.length && !running;
  const editableFields = (selectedEndpoint?.fields || []).filter(
    (field) => !(field.source === "site" && siteId),
  );

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
      if (key === "snippet") {
        toastSuccess(
          "Snippet copied. Playground key lasts 1 hour. Use a saved key for ongoing access.",
          7000,
        );
      }
    } catch (_) {}
  };

  const runRequest = async () => {
    if (!canRun || !selectedEndpoint || !selectedTokenHash) return;
    setRunning(true);
    setResponse(null);
    setOutputTab("response");
    try {
      let key = await ensurePlaygroundSession(selectedTokenHash);
      let res = await runExternalApiRequest({
        method: selectedEndpoint.method,
        path: runPath,
        apiKey: key,
        body: bodyPayload,
      });
      // Renew once if the hour-long playground token expired mid-session.
      if (res.status === 401) {
        key = await ensurePlaygroundSession(selectedTokenHash, { force: true });
        res = await runExternalApiRequest({
          method: selectedEndpoint.method,
          path: runPath,
          apiKey: key,
          body: bodyPayload,
        });
      }
      setResponse({
        status: res.status,
        data: res.data,
      });
    } catch (err) {
      setResponse({
        status: null,
        data: { error: err?.message || "Request failed" },
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-8 pb-8 lg:grid-cols-3">
      {/* Endpoint browser, grouped by section like the Changes lists */}
      <nav className="min-w-0 space-y-6 lg:col-span-1">
        {[...new Set(API_ENDPOINTS.map((e) => e.section))].map((section) => (
          <section key={section}>
            <h2 className={`mb-2 ${SECTION_LABEL}`}>{section}</h2>
            <div className="divide-y divide-theme-secondary overflow-hidden rounded-2xl border border-theme-secondary">
              {API_ENDPOINTS.filter((e) => e.section === section).map((endpoint) => (
                <button
                  key={endpoint.id}
                  type="button"
                  onClick={() => setSelectedEndpointId(endpoint.id)}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition ${
                    selectedEndpointId === endpoint.id
                      ? "bg-theme-secondary font-medium text-theme-text"
                      : "text-theme-text seo-row-hover"
                  }`}
                >
                  <MethodBadge method={endpoint.method} />
                  <span className="min-w-0 flex-1 truncate">{endpoint.label}</span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </nav>

      <div className="min-w-0 space-y-6 lg:col-span-2">
        {/* Request composer, same card treatment as the Changes composer */}
        <div className="rounded-2xl border border-theme-shadow bg-theme-main p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_16px_40px_-18px_rgba(0,0,0,0.35)] sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-theme-text">{selectedEndpoint?.label}</h2>
              <p className="mt-0.5 text-sm text-theme-secondary-text">{selectedEndpoint?.description}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {siteId && (
                <span className="inline-flex max-w-[12rem] items-center gap-1.5 rounded-full border border-theme-secondary px-2.5 py-1 text-xs font-medium text-theme-text">
                  <GlobeAltIcon className="h-3.5 w-3.5 shrink-0 text-theme-secondary-text" />
                  <span className="truncate">{currentProject?.name || siteId}</span>
                </span>
              )}
              {activeTokens.length > 0 && (
                <SelectPopover
                  value={selectedTokenHash}
                  onChange={setSelectedTokenHash}
                  options={activeTokens.map((token) => ({
                    id: token.hash,
                    label: token.name || token.hash,
                    note: `${token.token_prefix}…`,
                    icon: <KeyIcon className="h-3.5 w-3.5 shrink-0 text-theme-secondary-text" />,
                  }))}
                />
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-xl bg-theme-secondary py-1.5 pl-3 pr-1.5">
            <MethodBadge method={selectedEndpoint?.method || "GET"} />
            <code className="min-w-0 flex-1 truncate text-xs text-theme-text" title={displayUrl}>
              {displayUrl}
            </code>
            <span
              className="inline-flex shrink-0"
              data-tooltip-id="api-playground-run-tooltip"
              data-tooltip-content={runDisabledReason}
              data-tooltip-hidden={!runDisabledReason}
            >
              <button
                type="button"
                onClick={runRequest}
                disabled={!canRun}
                className={`inline-flex h-8 items-center gap-1.5 rounded-full bg-theme-primary px-4 text-sm font-semibold text-theme-button transition hover:opacity-90 disabled:opacity-40 ${
                  !canRun ? "pointer-events-none" : ""
                }`}
              >
                <PlayIcon className="h-3.5 w-3.5" />
                Run
              </button>
            </span>
            <Tooltip id="api-playground-run-tooltip" place="top" className="!text-xs !max-w-xs" />
          </div>

          {editableFields.length > 0 && (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {editableFields.map((field) => (
                <div key={field.key} className={field.type === "textarea" ? "sm:col-span-2" : ""}>
                  <label className="mb-1 block text-xs font-medium text-theme-secondary-text">
                    {field.label}
                    {field.required ? " *" : ""}
                  </label>
                  {field.type === "boolean" ? (
                    <label className="inline-flex items-center gap-2 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={fieldValues[field.key] === true}
                        onChange={(e) =>
                          setFieldValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                        }
                      />
                      <span>{fieldValues[field.key] === true ? "Yes" : "No"}</span>
                    </label>
                  ) : field.type === "textarea" ? (
                    <textarea
                      value={fieldValues[field.key] ?? ""}
                      placeholder={placeholderForParam(field.key)}
                      onChange={(e) =>
                        setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                      rows={4}
                      className={inputClass}
                    />
                  ) : (
                    <input
                      type="text"
                      value={fieldValues[field.key] ?? ""}
                      placeholder={placeholderForParam(field.key)}
                      onChange={(e) =>
                        setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                      className={`${inputClass} font-mono`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="mt-3 text-[11px] text-theme-secondary-text">
            {tokensLoaded && !activeTokens.length ? (
              <>
                You need an API key to run requests.{" "}
                <Link to="/api/keys/new/" className="text-theme-primary hover:underline">
                  Create one
                </Link>
              </>
            ) : (
              "Runs with a temporary key that expires in 1 hour. Use a saved key in your own code."
            )}
          </p>
        </div>

        {/* Response and code snippets share one tabbed panel */}
        <FolderTabs tabs={OUTPUT_TABS} activeId={outputTab} onChange={setOutputTab}>
          {outputTab === "response" ? (
            running ? (
              <div className="flex flex-col items-center justify-center gap-3 py-10">
                <WeaveLoader size={48} />
                <p className="text-sm text-theme-secondary-text">Running request…</p>
              </div>
            ) : response ? (
              <>
                <div className="mb-3 flex items-center gap-2">
                  <HttpStatus code={response.status} />
                  <CopyButton
                    className="ml-auto"
                    copied={copied === "response"}
                    onClick={() => copyText(JSON.stringify(response.data, null, 2), "response")}
                  />
                </div>
                <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-theme-text">
                  {JSON.stringify(response.data, null, 2)}
                </pre>
              </>
            ) : (
              <p className="py-10 text-center text-sm text-theme-secondary-text">
                Press Run to see the response here.
              </p>
            )
          ) : (
            <>
              <div className="mb-3 flex justify-end">
                <CopyButton
                  copied={copied === "snippet"}
                  onClick={() => copyText(snippet, "snippet")}
                />
              </div>
              <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-theme-text">
                {snippet}
              </pre>
            </>
          )}
        </FolderTabs>
      </div>
    </div>
  );
};

function CopyButton({ copied, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs font-medium hover:opacity-80 ${
        copied ? "text-emerald-500" : "text-theme-primary"
      } ${className}`}
    >
      <ClipboardDocumentIcon className="h-4 w-4" />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default ApiPlayground;
